"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { AddressSuggestion } from "../../../types/address-suggestion";
import { poiAddresses } from "../../../constants/poi-addresses";
import { AdresseFavorite } from "../../../types/models/adresse-favorite";
import { SuggestionAdresse } from "../../../types/models/suggestion-adresse";
import { useKmsService } from "../../../providers/kms-service-provider";
import Icon from "../../shared/ui/icon";

// Mapping type Google Places → icône, par inclusion (du plus spécifique au plus
// générique), avec une icône de repli.
const POI_ICON_BY_TYPE: [string, string][] = [
  ["airport", "flight"],
  ["train_station", "train"],
  ["bus_station", "directions_bus"],
  ["subway_station", "train"],
  ["hospital", "local_hospital"],
  ["pharmacy", "local_pharmacy"],
  ["school", "school"],
  ["university", "school"],
  ["restaurant", "restaurant"],
  ["cafe", "local_cafe"],
  ["hotel", "hotel"],
  ["lodging", "hotel"],
  ["museum", "museum"],
  ["stadium", "stadium"],
  ["park", "park"],
  ["bank", "account_balance"],
  ["store", "shopping_bag"],
  ["shopping_mall", "shopping_bag"],
];

const iconForPoiType = (t?: string | null): string =>
  (t && POI_ICON_BY_TYPE.find(([k]) => t.toLowerCase().includes(k))?.[1]) ||
  "place";

// SuggestionAdresse (backend) → item d'UI. Le placeId est toujours recopié dans
// l'adresse : c'est le fil rouge propagé jusqu'au calcul d'itinéraire.
const mapSuggestion = (s: SuggestionAdresse): AddressSuggestion => {
  const { adresse } = s;
  const isPoi = s.poi !== null;
  const addressLabel =
    [adresse.rue, adresse.numero].filter(Boolean).join(" ") ||
    adresse.ville ||
    adresse.rue;

  return {
    id: `place-${s.placeId}`,
    label: isPoi ? s.poi! : addressLabel,
    type: isPoi ? "poi" : "address",
    icon: isPoi ? iconForPoiType(s.poiType) : "location_on",
    adresse: {
      rue: adresse.rue,
      numero: adresse.numero,
      boite: adresse.boite ?? "",
      codePostal: adresse.codePostal,
      ville: adresse.ville,
      pays: adresse.pays,
      // Nom du lieu conservé pour l'afficher (POI sans rue : aéroport, gare…).
      ...(isPoi ? { nom: s.poi! } : {}),
      placeId: s.placeId,
    },
  };
};

type AddressCommandPaletteProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (address: AddressSuggestion) => void;
  favorites: AdresseFavorite[];
  isLoadingFavorites?: boolean;
};

type GroupConfig = {
  key: "favorites" | "pois" | "addresses";
  icon: string;
  iconColor: string;
  iconBg: string;
};

const GROUPS: GroupConfig[] = [
  {
    key: "favorites",
    icon: "star",
    iconColor: "text-amber-500",
    iconBg: "bg-amber-50 dark:bg-amber-950/30",
  },
  {
    key: "pois",
    icon: "place",
    iconColor: "text-blue-500",
    iconBg: "bg-blue-50 dark:bg-blue-950/30",
  },
  {
    key: "addresses",
    icon: "location_on",
    iconColor: "text-primary",
    iconBg: "bg-primary/10",
  },
];

const AddressCommandPalette = ({
  isOpen,
  onClose,
  onSelect,
  favorites,
  isLoadingFavorites,
}: AddressCommandPaletteProps) => {
  const t = useTranslations("address_palette");
  const { adresseService } = useKmsService();
  const [query, setQuery] = useState("");
  const [apiAddresses, setApiAddresses] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setApiAddresses([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setApiAddresses([]);
      setIsSearching(false);
      setSearchError(false);
      return;
    }

    const controller = new AbortController();
    setIsSearching(true);

    setSearchError(false);
    const timeoutId = setTimeout(async () => {
      try {
        const data = await adresseService.getSuggestions(
          trimmed,
          "fr",
          8,
          controller.signal,
        );
        setApiAddresses(data.map(mapSuggestion));
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("[AddressPalette] fetch error:", error);
          setApiAddresses([]);
          setSearchError(true);
        }
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [query, adresseService]);

  const favoriteSuggestions: AddressSuggestion[] = useMemo(
    () =>
      favorites.map((fav) => ({
        id: `fav-${fav.id}`,
        label: fav.nom || `${fav.adresse.rue} ${fav.adresse.numero}`,
        type: "favorite" as const,
        icon: "star",
        adresse: {
          rue: fav.adresse.rue,
          numero: fav.adresse.numero,
          boite: fav.adresse.boite ?? "",
          codePostal: fav.adresse.codePostal,
          ville: fav.adresse.ville,
          pays: fav.adresse.pays,
          ...(fav.nom ? { nom: fav.nom } : {}),
          placeId: fav.adresse.placeId,
        },
      })),
    [favorites],
  );

  const filteredResults = useMemo(() => {
    const searchInAddress = (
      item: AddressSuggestion,
      normalizedQuery: string,
    ) => {
      const searchableValue = [
        item.label,
        item.adresse.rue,
        item.adresse.numero,
        item.adresse.boite,
        item.adresse.codePostal,
        item.adresse.ville,
        item.adresse.pays,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableValue.includes(normalizedQuery);
    };

    const apiPois = apiAddresses.filter((a) => a.type === "poi");
    const apiPlainAddresses = apiAddresses.filter((a) => a.type !== "poi");

    if (!query.trim()) {
      return {
        favorites: favoriteSuggestions,
        pois: poiAddresses,
        addresses: [] as AddressSuggestion[],
      };
    }

    const normalizedQuery = query.trim().toLowerCase();

    return {
      favorites: favoriteSuggestions.filter((item) =>
        searchInAddress(item, normalizedQuery),
      ),
      pois: [
        ...poiAddresses.filter((item) =>
          searchInAddress(item, normalizedQuery),
        ),
        ...apiPois,
      ],
      addresses: apiPlainAddresses,
    };
  }, [query, favoriteSuggestions, apiAddresses]);

  const orderedGroups = useMemo(
    () =>
      GROUPS.filter((group) => {
        if (group.key === "favorites") {
          return filteredResults.favorites.length > 0 || isLoadingFavorites;
        }

        return filteredResults[group.key].length > 0;
      }),
    [filteredResults, isLoadingFavorites],
  );

  const hasResults = orderedGroups.some(
    (g) => filteredResults[g.key].length > 0,
  );

  const handleSelect = (address: AddressSuggestion) => {
    onSelect(address);
    onClose();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[1100]">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
        </TransitionChild>

        {/* Container : bottom sheet mobile, modal large tablette/desktop */}
        <div className="fixed inset-0 flex items-end sm:items-center sm:justify-center sm:p-6 lg:p-8">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:scale-95"
          >
            <DialogPanel
              className="
								w-full sm:max-w-2xl lg:max-w-3xl
								bg-white dark:bg-slate-900
								rounded-t-2xl sm:rounded-2xl
								shadow-xl
								border border-slate-200 dark:border-slate-700
								overflow-hidden flex flex-col
								h-[88dvh] sm:h-[min(76dvh,680px)] lg:h-[min(72dvh,720px)]
						"
            >
              {/* Handle mobile */}
              <div className="sm:hidden flex justify-center pt-2 pb-1">
                <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-700" />
              </div>

              {/* Barre de recherche — style cohérent avec tes Fields */}
              <div className="flex items-center gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-200 dark:border-slate-700">
                <Icon name="search" className=" text-xl text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("placeholder")}
                  className="flex-1 bg-transparent text-base text-slate-900 dark:text-white placeholder:text-slate-400 outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    aria-label={t("clear")}
                  >
                    <Icon name="close" className="text-base" />
                  </button>
                )}
              </div>

              {/* Résultats */}
              <div className="overflow-y-auto flex-1">
                {isLoadingFavorites && !query ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {t("loading")}
                    </p>
                  </div>
                ) : hasResults ? (
                  <div className="py-2 sm:py-3">
                    {orderedGroups.map((group) => {
                      const items = filteredResults[group.key];
                      if (items.length === 0) return null;

                      return (
                        <div key={group.key} className="mb-2 sm:mb-3 last:mb-0">
                          <div className="px-4 sm:px-5 py-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                              {t(group.key)}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            {items.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleSelect(item)}
                                className="
																	w-full flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-4
																	hover:bg-slate-50 dark:hover:bg-slate-800/50
																	active:bg-slate-100 dark:active:bg-slate-800
																	transition-colors text-left
															"
                              >
                                <div
                                  className={`flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-lg shrink-0 ${group.iconBg}`}
                                >
                                  <Icon
                                    name={item.icon}
                                    className={`text-lg sm:text-xl ${group.iconColor}`}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white truncate">
                                    {item.label}
                                  </p>
                                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate mt-0.5">
                                    {item.adresse.rue} {item.adresse.numero},{" "}
                                    {item.adresse.codePostal}{" "}
                                    {item.adresse.ville}
                                  </p>
                                </div>
                                <Icon
                                  name="chevron_right"
                                  className="text-slate-300 dark:text-slate-600 text-lg shrink-0"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {isSearching && (
                      <div className="flex items-center justify-center gap-2 py-4 text-xs text-slate-400">
                        <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        {t("searching")}
                      </div>
                    )}
                    {searchError && !isSearching && (
                      <div className="flex items-center gap-2 mx-4 sm:mx-5 my-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-xs text-red-600 dark:text-red-400">
                        <Icon name="wifi_off" className="text-sm" />
                        {t("search_unavailable")}
                      </div>
                    )}
                  </div>
                ) : isSearching ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {t("searching")}
                    </p>
                  </div>
                ) : searchError ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50 dark:bg-red-950/30 mb-3">
                      <Icon name="wifi_off" className="text-2xl text-red-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {t("search_service_down")}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t("check_connection")}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 mb-3">
                      <Icon
                        name="location_off"
                        className="text-2xl text-slate-400"
                      />
                    </div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {t("no_results")}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t("try_another_term")}
                    </p>
                  </div>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddressCommandPalette;
